import Heading from '@pelckmans/elementis/components/heading';
import Paragraph from '@pelckmans/elementis/components/paragraph';
import { Cell } from '@pelckmans/elementis/components/table';
import TextField from '@pelckmans/elementis/components/text-field';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ImportMembersContext } from '../contexts/ImportMembersContext';
import EmptyTable from './EmptyTable';
import SelectFieldToLink, { linkColumnOptions } from './SelectFieldToLink';

const TRANSLATION_KEY = 'PAGES.MY_GROUPS.MODALS.IMPORT_MEMBERS.LINK_MEMBERS_DATA';
const COLUMN_ORDER = [
  linkColumnOptions.FIRST_NAME,
  linkColumnOptions.LAST_NAME,
  linkColumnOptions.PASSWORD,
  linkColumnOptions.EMAIL,
  linkColumnOptions.USERNAME,
];

export default function LinkMembersData() {
  const { t } = useTranslation();

  const {
    fileMembersData,
    fileHeaderData,
    setColumnMapping,
    columnMapping,
    columnMappingByLinkedOption = {},
  } = useContext(ImportMembersContext);

  const handleOnChange = (LinkToColumnIndex: number, value: string) => {
    setColumnMapping(prev => ({
      ...prev,
      [LinkToColumnIndex]: value === linkColumnOptions.DONT_LINK ? undefined : value,
    }));
  };

  const hasMappedColumns = Object.keys(columnMappingByLinkedOption).length > 0;

  const slicedData = fileMembersData?.slice(0, 8);

  return (
    <div className="link-members-data">
      <div className="modal-content-text">
        <Heading level={4} removeSpacing="top">
          {t(`${TRANSLATION_KEY}.TITLE`)}
        </Heading>
        <Paragraph size="md" removeSpacing>
          {t(`${TRANSLATION_KEY}.DESCRIPTION`)}
        </Paragraph>
      </div>
      <div className="modal-content-form">
        {fileHeaderData?.map((col, index) => {
          return (
            <div key={`fields-row-${index}`} className="fields-row-container">
              <TextField
                className="field"
                labelText={index === 0 ? t(`${TRANSLATION_KEY}.FROM_FILE.LABEL`) : ''}
                disabled
                value={col}
                required
              />
              <SelectFieldToLink
                valuesByColumnIndex={columnMapping}
                onChange={handleOnChange}
                linkToColumnIndex={index}
              />
            </div>
          );
        })}
      </div>

      <div className="preview">
        <div className="subtexts">
          <Paragraph size="sm" removeSpacing>
            <b>{t(`${TRANSLATION_KEY}.PREVIEW`)}</b>
          </Paragraph>
          {hasMappedColumns && slicedData?.length === 8 && (
            <Paragraph size="sm" removeSpacing>
              {t(`${TRANSLATION_KEY}.FIRST_EIGHT_ROWS`)}
            </Paragraph>
          )}
        </div>
        {hasMappedColumns ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {COLUMN_ORDER.map(columnOption => {
                    const columnIndex = columnMappingByLinkedOption?.[columnOption];
                    if (!columnIndex) return null;

                    const emailColumn = columnOption === linkColumnOptions.EMAIL;

                    return (
                      <Cell.HeaderText
                        title={t(`${TRANSLATION_KEY}.TO_LINK.OPTIONS.${columnOption}`)}
                        key={`header-${columnIndex}-${columnOption}`}
                        minWidth={emailColumn ? 'md' : 'sm'}
                        maxWidth={emailColumn ? 'md' : 'sm'}
                      />
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {slicedData?.map((row, index) => (
                  <tr key={`row-${index}`}>
                    {COLUMN_ORDER.map(columnOption => {
                      const columnIndex = columnMappingByLinkedOption?.[columnOption];
                      if (!columnIndex) return null;

                      const emailColumn = columnOption === linkColumnOptions.EMAIL;

                      return (
                        <Cell
                          density="compact"
                          key={`cell-${columnIndex}-${columnOption}-${index}`}
                          minWidth={emailColumn ? 'md' : 'sm'}
                          maxWidth={emailColumn ? 'md' : 'sm'}
                        >
                          <Cell.Text title={row[Number(columnIndex)]} />
                        </Cell>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyTable />
        )}
      </div>
    </div>
  );
}
